import * as React from "react";
import {
  Card, CardHeader, List, ListItem, Typography, Box, Pagination, IconButton, Link
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { Link as RouterLink } from "react-router-dom";
import { gold, white } from "../../../../../getLPTheme";

type Item = {
  pointId: string;
  coordinates?: string;
  typeMarker?: string;
  name: string;
  address?: string;
  country?: string;
  state?: string;
  locate?: string;
  contact?: string;
  url?: string;
  discount?: string;
};

const PAGE_SIZE = 5;

// sm: columnas clave
const COLS_SM =
  "60px 140px minmax(180px,1fr) 140px 140px 140px minmax(120px,0.6fr)";

// md: agregamos address + url
const COLS_MD =
  "60px 140px minmax(200px,1fr) minmax(240px,1.2fr) 140px 140px 140px minmax(220px,1fr) minmax(120px,0.6fr)";

// lg: tabla completa
const COLS_LG =
  "60px 150px 150px 80px minmax(200px,1fr) minmax(260px,1.2fr) 140px 140px 140px 180px minmax(240px,1fr) minmax(120px,0.6fr)";

const GRID_GAP = 0;
const PAD_X = 4;

export default function ItemTable({ items }: { items: Item[] }) {
  const rows = items.map((it) => ({
    id: it.pointId,
    coordinates: it.coordinates ?? "-",
    typeMarker: it.typeMarker ?? "-",
    name: it.name,
    address: it.address ?? "-",
    country: it.country ?? "-",
    state: it.state ?? "-",
    locate: it.locate ?? "-",
    contact: it.contact ?? "-",
    url: it.url ?? "",
    discount: it.discount ?? "-",
  }));

  const [page, setPage] = React.useState(1);
  const start = (page - 1) * PAGE_SIZE;
  const pageRows = rows.slice(start, start + PAGE_SIZE);
  const pages = Math.ceil(rows.length / PAGE_SIZE);

  const show = {
    md: { display: { xs: "none", md: "block" } },
    lg: { display: { xs: "none", lg: "block" } },
  };
  

  return (
    <Card
      sx={{
        bgcolor: "#0B0F14",
        color: "#EAECEF",
        borderRadius: 3,
        border: "1px solid #2B3139",
      }}
    >
      <CardHeader
        title="Puntos registrados"
        sx={{
          "& .MuiCardHeader-title": { fontWeight: 600, fontSize: { xs: 18, sm: 20 } },
          px: PAD_X,
          py: 1.5,
          bgcolor: "#0B0F14",
          borderBottom: "1px solid #2B3139",
        }}
      />

      {/* =======================
          MOBILE (xs): sin tabla, sin scroll horizontal
         ======================= */}
      <Box sx={{ display: { xs: "block", sm: "none" } }}>
        <List disablePadding sx={{ width: "100%" }}>
          {pageRows.map((r) => (
            <ListItem
              key={r.id}
              sx={{
                width: "100%",
                display: "block",
                px: PAD_X,
                py: 1.25,
                borderBottom: "1px solid #2B3139",
                "&:hover": { bgcolor: "#0E141B" },
                boxSizing: "border-box",
              }}
            >
              {/* Header: Name + Edit */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                <Typography sx={{ fontWeight: 700, flex: 1, minWidth: 0 }}>
                  <Box
                    component="span"
                    sx={{
                      display: "block",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {r.name}
                  </Box>
                </Typography>

                <RouterLink to={`/admin/marker/${r.id}`} style={{ textDecoration: "none" }}>
                  <IconButton
                    size="small"
                    sx={{
                      border: "1px solid #2B3139",
                      bgcolor: "#0B0F14",
                      "& svg": { color: white[50] },
                      "&:hover": { bgcolor: "#10161D" },
                      flexShrink: 0,
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </RouterLink>
              </Box>

              <Typography variant="caption" sx={{ opacity: 0.8, display: "block", mt: 0.25 }}>
                {r.country} • {r.state} • {r.locate}
              </Typography>

              <Typography variant="caption" sx={{ opacity: 0.7, display: "block", mt: 0.5 }}>
                ID: {r.id} — {r.typeMarker} — {r.coordinates}
              </Typography>

              {r.url && (
                <Link
                  href={r.url}
                  target="_blank"
                  rel="noopener"
                  sx={{
                    color: gold[300],
                    fontWeight: 600,
                    display: "block",
                    mt: 0.75,
                    maxWidth: "100%",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {r.url}
                </Link>
              )}
            </ListItem>
          ))}
        </List>
      </Box>

      {/* =======================
          DESKTOP (sm+): tabla con scroll horizontal
         ======================= */}
      <Box sx={{ display: { xs: "none", sm: "block" } }}>
        <Box sx={{ overflowX: "auto", width: "100%", maxWidth: "100%", bgcolor: "#0B0F14" }}>
          <Box sx={{ minWidth: "max-content", display: "inline-block" }}>
            {/* HEADER (sm+) */}
            <Box
              sx={{
                width: "max-content",
                display: "grid",
                gridTemplateColumns: { md: COLS_MD, lg: COLS_LG },
                columnGap: GRID_GAP,
                px: PAD_X,
                py: 1,
                fontSize: 12,
                opacity: 0.7,
                borderBottom: "1px solid #2B3139",
                boxSizing: "border-box",
                "& > *": { textAlign: "center", whiteSpace: "nowrap" },
                bgcolor: "#0B0F14",
                position: "sticky",
                top: 0,
                zIndex: 1,
              }}
            >
              <Typography>Editar</Typography>
              <Typography>ID</Typography>
              <Typography>Nombre</Typography>
              <Typography>País</Typography>
              <Typography>Estado</Typography>
              <Typography>Localidad</Typography>
              <Typography>Beneficio</Typography>

              <Typography sx={show.md}>Dirección</Typography>
              <Typography sx={show.md}>URL</Typography>

              <Typography sx={show.lg}>Coord.</Typography>
              <Typography sx={show.lg}>Icon</Typography>
              <Typography sx={show.lg}>Contacto</Typography>
            </Box>

            {/* FILAS (sm+) */}
            <List disablePadding sx={{ width: "max-content" }}>
              {pageRows.map((r) => (
                <ListItem
                  key={r.id}
                  sx={{
                    width: "max-content",
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr auto",     // mobile cards
                      md: COLS_MD,        // tabla recién en md+
                      lg: COLS_LG,
                    },
                    columnGap: GRID_GAP,
                    alignItems: "center",
                    px: PAD_X,
                    py: 1.25,
                    borderBottom: "1px solid #2B3139",
                    "&:hover": { bgcolor: "#0E141B" },
                    boxSizing: "border-box",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <RouterLink to={`/admin/marker/${r.id}`} style={{ textDecoration: "none" }}>
                      <IconButton
                        size="small"
                        sx={{
                          border: "1px solid #2B3139",
                          bgcolor: "#0B0F14",
                          "& svg": { color: white[50] },
                          "&:hover": { bgcolor: "#10161D" },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </RouterLink>
                  </Box>

                  <Typography sx={{ textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {r.id}
                  </Typography>
                  <Typography sx={{ textAlign: "center", fontWeight: 600 }}>
                    {r.name}
                  </Typography>
                  <Typography sx={{ textAlign: "center" }}>{r.country}</Typography>
                  <Typography sx={{ textAlign: "center" }}>{r.state}</Typography>
                  <Typography sx={{ textAlign: "center" }}>{r.locate}</Typography>
                  <Typography sx={{ textAlign: "center", color: gold[300], fontWeight: 600 }}>
                    {r.discount}
                  </Typography>

                  <Typography sx={{ ...show.md, textAlign: "center" }}>{r.address}</Typography>

                  <Box
                    sx={{
                      ...show.md,
                      textAlign: "center",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 420,
                    }}
                  >
                    {r.url ? (
                      <Link href={r.url} target="_blank" rel="noopener" sx={{ color: gold[300], fontWeight: 600 }}>
                        {r.url}
                      </Link>
                    ) : (
                      <Typography>-</Typography>
                    )}
                  </Box>

                  <Typography sx={{ ...show.lg, textAlign: "center" }}>{r.coordinates}</Typography>
                  <Typography sx={{ ...show.lg, textAlign: "center" }}>{r.typeMarker}</Typography>
                  <Typography sx={{ ...show.lg, textAlign: "center" }}>{r.contact}</Typography>
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      </Box>

      {/* Paginación */}
      <Box
        sx={{
          width: "max-content",
          ml: "auto",
          px: 1.5,
          py: 1.25,
          borderTop: "1px solid #2B3139",
        }}
      >
        <Pagination
          count={pages}
          page={page}
          onChange={(_, v) => setPage(v)}
          sx={{
            "& .MuiPaginationItem-root": {
              bgcolor: "#0B0F14",
              border: "1px solid #2B3139",
              color: "white",
            },
            "& .Mui-selected": {
              bgcolor: `${gold[500]} !important`,
              color: "#0B0F14",
              borderColor: gold[500],
              "&:hover": { bgcolor: gold[500] },
            },
            "& .MuiPaginationItem-previousNext": { color: "white" },
          }}
        />
      </Box>
    </Card>
  );
}
